<template lang="pug">
#facility-status-tab-component
  .register-body.mt-4
    .statuses
      .status(v-for="status in statuses")
        // input type radio
        .radio-input(v-if="status.inputType == 0")
          FormInputRadio(v-model="status.answer" :options="status.options" :keyName="status.label" :name="status.name" 
            :validationRules="{ required: status.required }" :required="status.required")

        // input type multipletext
        .multipletext(v-if="status.inputType == 1")
          FormTextarea(v-model="status.answer" :key-name="status.label" :name="status.name" :placeholder="status.inputOptions.placeholder"
            :validation-rules="{ max: status.inputOptions.maxLength, required: status.required }" :maxlength="status.inputOptions.maxLength"
            :styles="{ maxWidth: '760px', width: '760px', minHeight: '160px' }")

      .error-message(v-show="errorMessage")
        span {{ errorMessage }}

  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.update-btn(@click="onUpdate(safetyConfirmsId, statuses, facilityStatus)" type="button") 更新
      button.btn.cancel-btn(@click="onCancel" type="button") キャンセル
  
  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import SystemError from "@/commons/models/SystemError";
import SafetyConfirmFacilityStatusesService, { SafetyConfirmFacilityStatusGetModel } from "@/commons/services/SafetyConfirmFacilityStatuses";
import FamilySafetyConfirmsTemplatesService, { FamilySafetyConfirmsTemplatesGetModel } from "@/commons/services/FamilySafetyConfirmsTemplates";
import FamilySafetyConfirmMastersService, {
  FamilySafetyConfirmMasterInputType,
  FamilySafetyConfirmMasterRadioInputOption,
  FamilySafetyConfirmMastersGetModel,
  FamilySafetyConfirmMasterType
} from "@/commons/services/FamilySafetyConfirmMasters";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

/**
 * ### 施設全体状況タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    ValidationAlert,
    FormInputRadio,
    FormTextarea
  },
  $_veeValidate: { validator: "new" }
})
export default class FacilityStatusTab extends Vue {
  /** 安否確認(家族)施設状況サービス */
  @Inject("SafetyConfirmFacilityStatusesService") safetyConfirmFacilityStatusesService!: SafetyConfirmFacilityStatusesService;
  /** 安否確認(家族)テンプレートサービス */
  @Inject("FamilySafetyConfirmsTemplatesService") familySafetyConfirmsTemplatesService!: FamilySafetyConfirmsTemplatesService;
  /** 安否確認(家族)で送信する質問内容のマスタサービス */
  @Inject("FamilySafetyConfirmMastersService") familySafetyConfirmMastersService!: FamilySafetyConfirmMastersService;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "読込み中...";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 災害ID */
  safetyConfirmsId: number = 0;
  /** 安否確認(家族)で送信する質問内容のマスタ */
  masters: Array<FamilySafetyConfirmMastersGetModel> = [];
  /** 安否確認(家族)テンプレート */
  template: FamilySafetyConfirmsTemplatesGetModel | null = null;
  /** 安否確認(家族)の施設状況 */
  facilityStatus: SafetyConfirmFacilityStatusGetModel | null = null;
  /** 画面入力用の安否確認(家族)施設状況 */
  statuses: Array<Status> = [];

  /** エラーメッセージ */
  @Prop({ required: true, type: String })
  errorMessage!: string;

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 施設状況を入力する項目のために安否確認(家族)テンプレートを取得する
   * - 施設状況の質問項目の情報のために安否確認(家族)で送信する質問内容のマスタサービスを取得する
   * - 前回入力内容が存在するかもしれないので、安否確認(家族)施設状況を取得する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const safetyConfirmsId: number = Number(this.$route.params.id);
    this.safetyConfirmsId = safetyConfirmsId;

    const masters: Array<FamilySafetyConfirmMastersGetModel> = await this.getFamilySafetyConfirmMasters();
    this.masters = masters;

    const facilityStatus: SafetyConfirmFacilityStatusGetModel | null = await this.getFacilityStatus(safetyConfirmsId);
    const answerMap: Map<number, string> = this.createAnswerMap(facilityStatus);
    this.facilityStatus = facilityStatus;

    const template: FamilySafetyConfirmsTemplatesGetModel = await this.getTemplate();
    const useIdMap: Map<number, boolean> = this.createUseIdMap(template);
    this.template = template;

    const statuses: Array<Status> = this.createFacilityStatus(masters, useIdMap, answerMap);
    this.statuses = statuses;

    this.isBusy = false;

    return;
  }

  /**
   * ### 安否確認(家族)で送信する質問内容のマスタを取得する
   */
  public async getFamilySafetyConfirmMasters(): Promise<Array<FamilySafetyConfirmMastersGetModel>> {
    const response: Array<FamilySafetyConfirmMastersGetModel> = await this.familySafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 安否確認(家族)の施設状況を取得する
   *
   * - 安否確認(家族)の施設状況は登録されていなければ404が返るため、その場合はnullを返す
   */
  public async getFacilityStatus(safetyConfirmsId: number): Promise<SafetyConfirmFacilityStatusGetModel | null> {
    let response: SafetyConfirmFacilityStatusGetModel | null = null;

    try {
      response = await this.safetyConfirmFacilityStatusesService.get(userStore, safetyConfirmsId);
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 404) {
        response = null;
      } else {
        throw error;
      }
    }

    return response;
  }

  /**
   * ### 安否確認(家族)の状況の回答をMAP化する
   *
   * - 安否確認(家族)の施設状況はnullがあり得る
   *   - nullの場合は空のMAPを返す
   * - nullではない場合は施設状況の回答の内容をMAP化して返す
   */
  public createAnswerMap(facilityStatus: SafetyConfirmFacilityStatusGetModel | null): Map<number, string> {
    let answerMap: Map<number, string> = new Map<number, string>();

    if (facilityStatus == null) {
      return answerMap;
    }

    for (const status of facilityStatus.statuses) {
      answerMap.set(status.id, status.answer);
    }

    return answerMap;
  }

  /**
   * ### 安否確認(家族)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<FamilySafetyConfirmsTemplatesGetModel> {
    const response: FamilySafetyConfirmsTemplatesGetModel = await this.familySafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### テンプレートの使用する質問のMAPを作る
   *
   * - テンプレートの使用するIDリストに格納されている質問のみ使用する
   */
  public createUseIdMap(template: FamilySafetyConfirmsTemplatesGetModel): Map<number, boolean> {
    let map: Map<number, boolean> = new Map<number, boolean>();

    for (const useId of template.useIds) {
      map.set(useId, true);
    }

    return map;
  }

  /**
   * ### 入力させる使用する質問リストを作成する
   *
   * - 入力種別がラジオボタンの質問は選択肢ラベルを作成する
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createFacilityStatus(masters: Array<FamilySafetyConfirmMastersGetModel>, useIdMap: Map<number, boolean>, answerMap: Map<number, string>): Array<Status> {
    let statuses: Array<Status> = [];

    for (const master of masters) {
      if (master.type != FamilySafetyConfirmMasterType.FACILITY) {
        continue;
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      if (use != undefined) {
        let options: Array<{ value: number | string; label: string }> = [];

        // 入力種別がラジオボタンの場合は選択肢を作成する
        if (master.inputType == FamilySafetyConfirmMasterInputType.RADIO) {
          const inputOptions: FamilySafetyConfirmMasterRadioInputOption = master.inputOptions as FamilySafetyConfirmMasterRadioInputOption;
          options = inputOptions.choices;
        }

        // 既に入力されている場合はその値を使用する
        let answer: string | undefined = answerMap.get(master.id);

        const status: Status = {
          ...master,
          answer: answer ? answer : (undefined as any),
          options: options
        };
        statuses.push(status);
      }
    }

    return statuses;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public async onUpdate(safetyConfirmsId: number, statuses: Array<Status>, facilityStatus: SafetyConfirmFacilityStatusGetModel | null): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult == true) {
      this.showValidationAlert = false;

      const model: FacilityStatusTabOnUpdateModel = {
        updateStatuses: statuses,
        safetyConfirmsId: safetyConfirmsId,
        beforeUpdateFacilityStatus: facilityStatus
      };
      this.$emit("onUpdate", model);
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public onCancel(): void {
    this.$emit("onCancel");
    return;
  }
}

export interface Status extends FamilySafetyConfirmMastersGetModel {
  /** 回答内容 */
  answer: string;
  /** ラジオボタンの場合の選択肢 */
  options?: Array<{ value: number | string; label: string }>;
}

export interface FacilityStatusTabOnUpdateModel {
  /** 災害ID */
  safetyConfirmsId: number;
  /** 施設状況 */
  updateStatuses: Array<Status>;
  /** 更新前施設状況 */
  beforeUpdateFacilityStatus: SafetyConfirmFacilityStatusGetModel | null;
}
</script>

<style lang="scss" scoped>
#facility-status-tab-component {
  position: relative;

  .register-body {
    min-height: 240px;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
